/**
 * Add Members Modal Component
 * 
 * Modal for adding new members to a group chat.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Input,
  List,
  Avatar,
  Checkbox,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  App,
} from 'antd';
import { SearchOutlined, UserAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { User, ChatScope, Conversation } from '@municipality/types';
import { chatService } from '@municipality/chat';

const { Text } = Typography;

/**
 * Get initials from a name
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a consistent color from a string
 */
const getAvatarColor = (name: string): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
    '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

const UserAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor?: string }>`
  background-color: ${(props) => props.$bgColor || 'transparent'} !important;
  color: #fff !important;
  font-weight: 500 !important;
  font-size: 13px !important;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 24px;
`;

const UserList = styled(List<User>)`
  max-height: 400px;
  overflow: auto;
`;

const ClickableListItem = styled(List.Item)`
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const UserEmail = styled(Text)`
  font-size: 12px;
  color: #999;
`;

interface AddMembersModalProps {
  open: boolean;
  conversation: Conversation | null;
  currentUserId: string;
  scope: ChatScope;
  municipalityId?: string;
  onClose: () => void;
  onMembersAdded?: () => void;
}

export const AddMembersModal: React.FC<AddMembersModalProps> = ({
  open,
  conversation,
  currentUserId,
  scope,
  municipalityId,
  onClose,
  onMembersAdded,
}) => {
  const { t } = useTranslation('chat');
  const { message } = App.useApp();
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Load available users (excluding current participants)
  const loadUsers = useCallback(async () => {
    if (!conversation || conversation.type !== 'group') return;

    setIsLoading(true);
    try {
      const availableUsers = await chatService.getAvailableUsers(
        currentUserId,
        scope,
        municipalityId
      );
      
      // Filter out users who are already participants
      // Always use the latest conversation.participantIds from props to ensure accuracy
      // Create a fresh array reference to avoid stale closure issues
      const currentParticipantIds = conversation.participantIds ? [...conversation.participantIds] : [];
      const filteredUsers = availableUsers.filter(
        (user) => !currentParticipantIds.includes(user.id)
      );
      
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      message.error(t('addMembersModal.loadError') || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [conversation, currentUserId, scope, municipalityId, t, message]);

  // Track participantIds as a string to detect changes reliably
  // Sort to ensure consistent comparison even if order changes
  const participantIdsKey = conversation?.participantIds 
    ? JSON.stringify([...conversation.participantIds].sort())
    : '';

  useEffect(() => {
    if (open && conversation) {
      // Always reload users when modal opens or when participantIds change
      loadUsers();
      setSearchQuery('');
      setSelectedUsers([]);
    }
  }, [open, conversation, participantIdsKey, loadUsers]);

  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
    );
  });

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddMembers = async () => {
    if (!conversation || selectedUsers.length === 0) return;

    setIsAdding(true);
    try {
      await chatService.addParticipantsToGroup(
        conversation.id,
        selectedUsers,
        currentUserId
      );
      
      message.success(
        t('addMembersModal.success', { count: selectedUsers.length }) || 
        `Added ${selectedUsers.length} member(s) to the group`
      );
      
      setSelectedUsers([]);
      onMembersAdded?.();
      onClose();
    } catch (error) {
      console.error('Failed to add members:', error);
      message.error(
        t('addMembersModal.error') || 
        'Failed to add members to the group'
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedUsers([]);
    onClose();
  };

  if (!conversation || conversation.type !== 'group') {
    return null;
  }

  return (
    <Modal
      title={t('addMembersModal.title') || 'Add Members'}
      open={open}
      onCancel={handleClose}
      footer={
        <Space>
          <Button onClick={handleClose}>
            {t('buttons.cancel', { ns: 'common' }) || 'Cancel'}
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={handleAddMembers}
            disabled={selectedUsers.length === 0}
            loading={isAdding}
          >
            {t('addMembersModal.addButton', { count: selectedUsers.length }) || 
             `Add ${selectedUsers.length > 0 ? selectedUsers.length : ''} member(s)`}
          </Button>
        </Space>
      }
      width={500}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          placeholder={t('addMembersModal.searchPlaceholder') || 'Search users...'}
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />

        {isLoading ? (
          <LoadingContainer>
            <Spin />
          </LoadingContainer>
        ) : filteredUsers.length === 0 ? (
          <Empty 
            description={
              searchQuery 
                ? t('addMembersModal.noResults') || 'No users found'
                : t('addMembersModal.noUsers') || 'No users available to add'
            } 
          />
        ) : (
          <UserList
            dataSource={filteredUsers}
            renderItem={(user: User) => (
              <ClickableListItem onClick={() => handleUserToggle(user.id)}>
                <List.Item.Meta
                  avatar={
                    <UserAvatar
                      src={user.photoURL}
                      size={40}
                      $bgColor={user.photoURL ? undefined : getAvatarColor(user.displayName || user.email)}
                    >
                      {!user.photoURL && getInitials(user.displayName || user.email)}
                    </UserAvatar>
                  }
                  title={
                    <Space>
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Text strong={selectedUsers.includes(user.id)}>
                        {user.displayName || user.email}
                      </Text>
                    </Space>
                  }
                  description={<UserEmail>{user.email}</UserEmail>}
                />
              </ClickableListItem>
            )}
          />
        )}
      </Space>
    </Modal>
  );
};

